import React, { useState } from 'react';
import { useTranslation } from '../../utils/i18nStub';

const educationLevels = [
  { value: 'phd', label: 'الدكتوراه' },
  { value: 'masters', label: 'الماجستير' },
  { value: 'bachelor', label: 'البكالوريوس' },
  { value: 'diploma', label: 'الدبلوم' },
  { value: 'high_school', label: 'الثانوية العامة' },
];

const gradeOptions = [
  { value: 'excellent', label: 'ممتاز' },
  { value: 'very_good', label: 'جيد جدًا' },
  { value: 'good', label: 'جيد' },
  { value: 'pass', label: 'مقبول' },
  { value: 'honors', label: 'مع مرتبة الشرف' },
];

const issuerTypes = [
  { value: 'university', label: 'جامعة' },
  { value: 'academy', label: 'أكاديمية' },
  { value: 'institute', label: 'معهد' },
  { value: 'other', label: 'جهة أخرى' },
];

const EmployeeEducationInfo = () => {
  const { t } = useTranslation('education');

  const [highestQualification, setHighestQualification] = useState('bachelor');
  const [fieldOfStudy, setFieldOfStudy] = useState('');
  const [issuer, setIssuer] = useState('');
  const [issuerType, setIssuerType] = useState('university');
  const [earningDate, setEarningDate] = useState('');
  const [grade, setGrade] = useState('excellent');
  const [attachmentName, setAttachmentName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [snapshot, setSnapshot] = useState({ highestQualification, fieldOfStudy, issuer, issuerType, earningDate, grade, attachmentName });

  const handleAttachmentChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setAttachmentName('');
      return;
    }
    setAttachmentName(file.name);
  };

  const captureSnapshot = () => setSnapshot({ highestQualification, fieldOfStudy, issuer, issuerType, earningDate, grade, attachmentName });
  const startEdit = () => { captureSnapshot(); setIsEditing(true); };
  const handleCancel = () => {
    setHighestQualification(snapshot.highestQualification);
    setFieldOfStudy(snapshot.fieldOfStudy);
    setIssuer(snapshot.issuer);
    setIssuerType(snapshot.issuerType);
    setEarningDate(snapshot.earningDate);
    setGrade(snapshot.grade);
    setAttachmentName(snapshot.attachmentName);
    setIsEditing(false);
  };
  const handleSave = () => { captureSnapshot(); setIsEditing(false); };

  return (
    <div className="space-y-6 text-right" dir="rtl">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {t('education.title', 'المؤهلات العلمية')}
        </h2>
        {!isEditing ? (
          <button
            type="button"
            onClick={startEdit}
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {t('edit', 'تعديل')}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              className="rounded-xl bg-sa-600 text-white px-4 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors"
            >
              {t('save', 'حفظ')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {t('cancel', 'إلغاء')}
            </button>
          </div>
        )}
      </div>

      <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">
              {t('education.highest', 'أعلى مؤهل علمي')}
            </label>
            <select
              value={highestQualification}
              onChange={(e) => setHighestQualification(e.target.value)}
              className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              disabled={!isEditing}
            >
              {educationLevels.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">
              {t('education.field', 'التخصص / مجال الدراسة')}
            </label>
            <input
              type="text"
              value={fieldOfStudy}
              onChange={(e) => setFieldOfStudy(e.target.value)}
              placeholder={t('education.field_placeholder', 'مثال: إدارة أعمال، علوم حاسب')}
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">
              {t('education.issuer', 'الجهة المانحة')}
            </label>
            <input
              type="text"
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
              placeholder={t('education.issuer_placeholder', 'مثال: جامعة الملك سعود')}
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">
              {t('education.issuer_type', 'تصنيف الجهة المانحة')}
            </label>
            <select
              value={issuerType}
              onChange={(e) => setIssuerType(e.target.value)}
              className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              disabled={!isEditing}
            >
              {issuerTypes.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">
              {t('education.earning_date', 'تاريخ الحصول على المؤهل')}
            </label>
            <input
              type="date"
              value={earningDate}
              onChange={(e) => setEarningDate(e.target.value)}
              className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              readOnly={!isEditing}
              disabled={!isEditing}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-500 mb-2">
              {t('education.grade', 'التقدير / المعدل')}
            </label>
            <select
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              disabled={!isEditing}
            >
              {gradeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6">
          <label className="text-sm font-semibold text-gray-500 mb-2 block">
            {t('education.attachment', 'إرفاق المستند')}<span className="text-gray-400"> ({t('education.allowed', 'PDF أو صورة')})</span>
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center bg-gray-50">
            <p className="text-sm text-gray-600 mb-4">
              {t('education.attachment_hint', 'قم بإرفاق صورة من الشهادة أو كشف الدرجات المعتمد من الجهة المانحة.')}
            </p>
            <label className="inline-flex items-center gap-2 rounded-xl bg-sa-600 text-white px-5 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors cursor-pointer">
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleAttachmentChange} disabled={!isEditing} />
              {t('education.upload', 'اختيار ملف')}
            </label>
            {attachmentName && (
              <p className="mt-3 text-sm text-gray-700">
                {t('education.selected_file', 'الملف المحدد')}: {attachmentName}
              </p>
            )}
          </div>
        </div>

        {isEditing && (
          <footer className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-6">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {t('education.cancel', 'إلغاء')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-xl bg-sa-600 text-white px-5 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors disabled:opacity-50"
              disabled={!fieldOfStudy || !issuer || !earningDate}
            >
              {t('education.save', 'حفظ التغييرات')}
            </button>
          </footer>
        )}
      </section>
    </div>
  );
};

export default EmployeeEducationInfo;
